'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { FormImageUpload } from '@/components/form/form-image-upload'
import { useTranslation } from 'react-i18next'

const schema = z.object({
  displayName: z.string().min(1, 'Display name is required').trim(),
  logoUrl: z.string().optional(),
  checkoutInstructions: z.string(),
  thankYouTitle: z.string(),
  thankYouSubtitle: z.string(),
  thankYouBody: z.string(),
  thankYouBadgeLabel: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

const defaultCheckoutInstructions =
  'Use order number {orderNumber} as your payment reference.\n\n(Replace this text with your real payment instructions.)'

interface CustomPaymentFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

export function CustomPaymentForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: CustomPaymentFormProps) {
  const { t } = useTranslation('integrations')
  const settings = defaultValues.settings ?? {}

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      displayName: defaultValues.displayName ?? '',
      logoUrl: defaultValues.logoUrl ?? '',
      checkoutInstructions: settings.checkoutInstructions ?? defaultCheckoutInstructions,
      thankYouTitle: settings.thankYouTitle ?? 'Thank you for your order',
      thankYouSubtitle:
        settings.thankYouSubtitle ?? 'Complete your payment using the instructions below.',
      thankYouBody: settings.thankYouBody ?? '',
      thankYouBadgeLabel: settings.thankYouBadgeLabel ?? 'Awaiting payment',
    },
  })

  const handleFormSubmit = async (data: FormValues) => {
    await onSubmit({
      credentials: {},
      settings: {
        checkoutInstructions: data.checkoutInstructions,
        thankYouTitle: data.thankYouTitle,
        thankYouSubtitle: data.thankYouSubtitle,
        thankYouBody: data.thankYouBody,
        thankYouBadgeLabel: data.thankYouBadgeLabel || undefined,
      },
      displayName: data.displayName,
      logoUrl: data.logoUrl?.trim() ? data.logoUrl.trim() : undefined,
    })
  }

  return (
    <form id="integration-config-form" onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('customPayment.checkoutLabelTitle')}</CardTitle>
          <CardDescription>
            {t('customPayment.checkoutLabelDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="displayName">{t('customPayment.displayName')}</Label>
            <Input
              id="displayName"
              placeholder={t('customPayment.displayNamePlaceholder')}
              disabled={isLoading}
              {...form.register('displayName')}
            />
            {form.formState.errors.displayName && (
              <p className="text-xs text-destructive">{form.formState.errors.displayName.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>{t('customPayment.logoOptional')}</Label>
            <FormImageUpload
              id="logoUrl"
              name="logoUrl"
              url={form.watch('logoUrl') || null}
              onImageChange={(url) => form.setValue('logoUrl', url ?? '', { shouldValidate: true })}
            />
            {form.formState.errors.logoUrl && (
              <p className="text-xs text-destructive">{form.formState.errors.logoUrl.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('customPayment.checkoutInstructionsTitle')}</CardTitle>
          <CardDescription>
            {t('customPayment.checkoutInstructionsDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={8}
            className="font-mono text-sm"
            disabled={isLoading}
            {...form.register('checkoutInstructions')}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('customPayment.thankYouPageTitle')}</CardTitle>
          <CardDescription>
            {t('customPayment.thankYouPageDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="thankYouTitle">{t('customPayment.title')}</Label>
            <Input id="thankYouTitle" disabled={isLoading} {...form.register('thankYouTitle')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="thankYouSubtitle">{t('customPayment.subtitle')}</Label>
            <Input id="thankYouSubtitle" disabled={isLoading} {...form.register('thankYouSubtitle')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="thankYouBadgeLabel">{t('customPayment.statusBadge')}</Label>
            <Input id="thankYouBadgeLabel" disabled={isLoading} {...form.register('thankYouBadgeLabel')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="thankYouBody">{t('customPayment.body')}</Label>
            <Textarea id="thankYouBody" rows={6} className="text-sm" disabled={isLoading} {...form.register('thankYouBody')} />
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
